import { useState, useEffect } from "react";
import { API, authFetch, useAuth } from "@/App";
import { toast } from "sonner";
import { FileText, Plus, Download, Trash2, Send, Camera, Eye, Settings, Users } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mutate } from "@/lib/apiMutate";

const CATEGORIES = ["General", "Product Catalog", "Price List", "Technical Specs", "Company Profile", "Scheme Details"];
const REGION_OPTIONS = [
  { value: "ALL", label: "All Regions" },
  { value: "GJ", label: "Gujarat" },
  { value: "RJ", label: "Rajasthan" },
];

/** PreviewFrame — fetches the PDF with auth token and renders via a blob URL.
 * This avoids the Mixed-Content browser block that happens when the backend's
 * preview_url is HTTP while the page is HTTPS.
 */
const PreviewFrame = ({ brochureId }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let revoked = false;
    let url = null;
    (async () => {
      try {
        const res = await authFetch(`${API}/brochures/${brochureId}/preview`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (revoked) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (e) {
        setErr(e.message || "Failed to load preview");
      }
    })();
    return () => { revoked = true; if (url) URL.revokeObjectURL(url); };
  }, [brochureId]);
  if (err) return <div className="p-8 text-center text-sm text-red-600">Failed to load preview: {err}</div>;
  if (!blobUrl) return <div className="p-8 text-center"><div className="w-6 h-6 spinner mx-auto" /></div>;
  return <iframe src={blobUrl} title="Brochure preview" className="w-full h-full border-0" data-testid="preview-iframe" />;
};



const BrochuresPage = () => {
  const { user } = useAuth();
  const isAdmin = ["SUPER_ADMIN", "SALES_ADMIN"].includes(user?.role);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [brochures, setBrochures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "General", visible_regions: ["ALL"], visible_employee_ids: [] });
  const [file, setFile] = useState(null);
  const [showSend, setShowSend] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const [showVisibility, setShowVisibility] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDealers, setSelectedDealers] = useState([]);

  const fetchBrochures = async () => {
    try {
      const res = await authFetch(`${API}/brochures`);
      const data = await res.json();
      if (data.success) setBrochures(data.data || []);
    } catch { toast.error("Failed to load brochures"); }
    finally { setLoading(false); }
  };

  const fetchEmployees = async () => {
    try {
      const res = await authFetch(`${API}/employees?status=ACTIVE`);
      const data = await res.json();
      if (data.success) setEmployees(data.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchBrochures(); if (isAdmin) fetchEmployees(); }, [isAdmin]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please select a file"); return; }
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const upRes = await authFetch(`${API}/upload-multiple?category=brochures`, { method: "POST", body: fd });
      if (!upRes.ok) { toast.error(`Upload failed (${upRes.status})`); return; }
      const upData = await upRes.json();
      const storage = upData.data?.[0];
      if (!upData.success || !storage?.storage_path) {
        toast.error("Upload returned no storage path — aborting");
        return;
      }
      const r = await mutate(authFetch, `${API}/brochures`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          file_url: storage.storage_path,
          file_path: storage.storage_path,
          file_size: storage.size || file.size,
          original_filename: storage.original_filename || file.name,
        }),
      }, { successMessage: "Brochure uploaded", failureMessage: "Failed to create brochure" });
      if (!r.ok) return;
      setShowAdd(false);
      setForm({ title: "", description: "", category: "General", visible_regions: ["ALL"], visible_employee_ids: [] });
      setFile(null);
      fetchBrochures();
    } finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this brochure? This cannot be undone.")) return;
    const res = await authFetch(`${API}/brochures/${id}`, { method: "DELETE" });
    if (!res.ok) { toast.error(`Failed to delete (${res.status})`); return; }
    setBrochures((prev) => prev.filter((b) => b.brochure_id !== id));
    toast.success("Brochure deleted");
  };

  const openSendDialog = async (brochure) => {
    setShowSend(brochure);
    setSelectedDealers([]);
    try {
      const res = await authFetch(`${API}/parties?approval_status=APPROVED`);
      const data = await res.json();
      if (data.success) setDealers(data.data.filter(p => p.party_type === "DEALER"));
    } catch { /* ignore */ }
  };

  const handleSend = async () => {
    if (!showSend || selectedDealers.length === 0) return;
    const res = await authFetch(`${API}/brochures/${showSend.brochure_id}/send`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ party_ids: selectedDealers }),
    });
    const data = await res.json();
    if (data.success) {
      toast.success(`Sent to ${data.data.sent_count} dealer(s)`);
      setShowSend(null);
    }
  };

  const saveVisibility = async () => {
    const r = await mutate(authFetch, `${API}/brochures/${showVisibility.brochure_id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        visible_regions: showVisibility.visible_regions,
        visible_employee_ids: showVisibility.visible_employee_ids,
      }),
    }, { successMessage: "Visibility updated", failureMessage: "Failed to update visibility" });
    if (!r.ok) return;
    setShowVisibility(null);
    fetchBrochures();
  };

  const toggleEmployeeInVisibility = (empId) => {
    const ids = showVisibility.visible_employee_ids || [];
    const next = ids.includes(empId) ? ids.filter(x => x !== empId) : [...ids, empId];
    setShowVisibility({ ...showVisibility, visible_employee_ids: next });
  };

  return (
    <div className="space-y-6" data-testid="brochures-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Brochures</h1>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin ? "Upload, preview, and control visibility per region or employee" : "Preview or download brochures assigned to you"}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="upload-brochure-btn">
            <Plus className="w-4 h-4 mr-2" /> Upload Brochure
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="w-8 h-8 spinner mx-auto" /></div>
      ) : brochures.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No brochures available</p>
          {isAdmin && <p className="text-xs text-slate-400 mt-1">Upload one to get started</p>}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {brochures.map((b) => (
            <Card key={b.brochure_id} className="p-4 hover:shadow-md transition-shadow" data-testid={`brochure-${b.brochure_id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-[#8B1A1A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">{b.title}</h3>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{b.description || "No description"}</p>
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{b.category}</Badge>
                    {(b.visible_regions || ["ALL"]).map((r) => (
                      <Badge key={r} className="text-[10px] bg-blue-50 text-blue-700 border-blue-200 border" data-testid={`region-${b.brochure_id}-${r}`}>
                        {r === "ALL" ? "All Regions" : r === "GJ" ? "Gujarat" : r === "RJ" ? "Rajasthan" : r}
                      </Badge>
                    ))}
                    {(b.visible_employee_ids || []).length > 0 && (
                      <Badge className="text-[10px] bg-purple-50 text-purple-700 border-purple-200 border">
                        {b.visible_employee_ids.length} employees
                      </Badge>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">
                    by {b.created_by_name}
                    {(b.updated_at || b.uploaded_at || b.created_at) && (
                      <span className="ml-1.5 inline-flex items-center gap-1 text-slate-500 dark:text-slate-400" data-testid={`brochure-stamp-${b.brochure_id}`}>
                        · last updated {new Date(b.updated_at || b.uploaded_at || b.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1.5 mt-3 pt-3 border-t">
                {b.preview_url && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowPreview(b)} data-testid={`preview-${b.brochure_id}`}>
                    <Eye className="w-3 h-3 mr-1" /> Preview
                  </Button>
                )}
                {(b.download_url || b.file_url) && (
                  <a href={`${API}/brochures/${b.brochure_id}/download`} target="_blank" rel="noopener noreferrer" onClick={async (e) => {
                    // Use authenticated fetch so the token header is sent; fallback to direct anchor for external URLs
                    if ((b.file_path || b.file_url || "").startsWith("http")) return; // external — let browser handle
                    e.preventDefault();
                    const res = await authFetch(`${API}/brochures/${b.brochure_id}/download`);
                    if (!res.ok) { toast.error(`Download failed (${res.status})`); return; }
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${b.title}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}>
                    <Button size="sm" variant="outline" className="h-7 text-xs w-full" data-testid={`download-${b.brochure_id}`}>
                      <Download className="w-3 h-3 mr-1" /> Download
                    </Button>
                  </a>
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openSendDialog(b)} data-testid={`send-${b.brochure_id}`}>
                  <Send className="w-3 h-3 mr-1" /> Send
                </Button>
                {isAdmin && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowVisibility({ ...b, visible_regions: b.visible_regions || ["ALL"], visible_employee_ids: b.visible_employee_ids || [] })} data-testid={`visibility-${b.brochure_id}`}>
                    <Settings className="w-3 h-3 mr-1" /> Visibility
                  </Button>
                )}
                {isSuperAdmin && (
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50 col-span-2" onClick={() => handleDelete(b.brochure_id)} data-testid={`delete-${b.brochure_id}`}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg" data-testid="upload-brochure-dialog">
          <DialogHeader><DialogTitle>Upload Brochure</DialogTitle></DialogHeader>
          <form onSubmit={handleUpload} className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Title</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1" data-testid="brochure-title" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full mt-1 px-3 py-2 border border-slate-200 text-sm rounded-md" data-testid="brochure-desc" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1" data-testid="brochure-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Visible to Regions</label>
              <div className="flex gap-2 mt-1">
                {REGION_OPTIONS.map((r) => {
                  const active = form.visible_regions.includes(r.value);
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => {
                        let next;
                        if (r.value === "ALL") {
                          next = ["ALL"];
                        } else {
                          next = form.visible_regions.filter(x => x !== "ALL");
                          next = active ? next.filter(x => x !== r.value) : [...next, r.value];
                          if (next.length === 0) next = ["ALL"];
                        }
                        setForm({ ...form, visible_regions: next });
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${active ? "bg-[#8B1A1A] text-white border-[#8B1A1A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                      data-testid={`region-chip-${r.value}`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">File (PDF, Image)</label>
              <label className="flex items-center gap-2 mt-1 px-3 py-3 border border-dashed border-slate-300 rounded-md cursor-pointer hover:border-[#8B1A1A] transition-colors">
                <Camera className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">{file ? file.name : "Select file..."}</span>
                <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <Button type="submit" disabled={creating} className="w-full btn-primary" data-testid="submit-brochure">
              {creating ? "Uploading..." : "Upload Brochure"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog — fetch as blob so the token is honoured AND the iframe
          src is a blob URL (no mixed-content issues). */}
      <Dialog open={!!showPreview} onOpenChange={(v) => !v && setShowPreview(null)}>
        <DialogContent className="max-w-5xl h-[90vh] p-0 overflow-hidden" data-testid="preview-dialog">
          <DialogHeader className="px-6 py-3 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#8B1A1A]" /> {showPreview?.title}
            </DialogTitle>
          </DialogHeader>
          {showPreview && (
            <PreviewFrame brochureId={showPreview.brochure_id} />
          )}
        </DialogContent>
      </Dialog>

      {/* Visibility Dialog */}
      <Dialog open={!!showVisibility} onOpenChange={(v) => !v && setShowVisibility(null)}>
        <DialogContent className="max-w-md" data-testid="visibility-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-[#8B1A1A]" /> Brochure visibility</DialogTitle></DialogHeader>
          {showVisibility && (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-slate-700 mb-2">Regions</p>
                <div className="flex gap-2 flex-wrap">
                  {REGION_OPTIONS.map((r) => {
                    const active = showVisibility.visible_regions.includes(r.value);
                    return (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => {
                          let next;
                          if (r.value === "ALL") next = ["ALL"];
                          else {
                            next = showVisibility.visible_regions.filter(x => x !== "ALL");
                            next = active ? next.filter(x => x !== r.value) : [...next, r.value];
                            if (next.length === 0) next = ["ALL"];
                          }
                          setShowVisibility({ ...showVisibility, visible_regions: next });
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border ${active ? "bg-[#8B1A1A] text-white border-[#8B1A1A]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                        data-testid={`visibility-region-${r.value}`}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-700 mb-2">
                  Specific employees ({(showVisibility.visible_employee_ids || []).length} selected — leave empty to show to all eligible)
                </p>
                <div className="max-h-52 overflow-y-auto border rounded-md p-2 space-y-1">
                  {employees.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No employees found</p>}
                  {employees.map((e) => {
                    const checked = (showVisibility.visible_employee_ids || []).includes(e.employee_id);
                    return (
                      <label key={e.employee_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer text-xs">
                        <input type="checkbox" checked={checked} onChange={() => toggleEmployeeInVisibility(e.employee_id)} data-testid={`vis-emp-${e.employee_id}`} />
                        <span className="flex-1">{e.full_name}</span>
                        <Badge variant="outline" className="text-[10px]">{e.role || e.designation}</Badge>
                      </label>
                    );
                  })}
                </div>
              </div>
              <Button onClick={saveVisibility} className="w-full btn-primary" data-testid="save-visibility-btn">Save visibility</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Dialog */}
      <Dialog open={!!showSend} onOpenChange={() => setShowSend(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Brochure to Dealers</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 mb-3">Select dealers to send "{showSend?.title}" to:</p>
          <div className="max-h-60 overflow-y-auto space-y-1 border rounded-md p-2">
            {dealers.length === 0 && <p className="text-xs text-slate-400 text-center py-2">No approved dealers</p>}
            {dealers.map(d => (
              <label key={d.party_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                <input type="checkbox" checked={selectedDealers.includes(d.party_id)}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedDealers([...selectedDealers, d.party_id]);
                    else setSelectedDealers(selectedDealers.filter(id => id !== d.party_id));
                  }} className="rounded" />
                <span>{d.firm_name}</span>
                <span className="text-xs text-slate-400 ml-auto">{d.party_code}</span>
              </label>
            ))}
          </div>
          <Button onClick={handleSend} disabled={selectedDealers.length === 0} className="w-full btn-primary mt-2" data-testid="send-brochure-submit">
            <Send className="w-4 h-4 mr-2" /> Send to {selectedDealers.length} Dealer(s)
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrochuresPage;
